import React, { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Headphones, ArrowDown, Instagram, Youtube, Music2 } from "lucide-react";
import { BRAND, STREAMING } from "../data/content";
import { Waveform } from "./Waveform";

/**
 * Masked line reveal — each line is clipped inside overflow-hidden
 * and translated up from below with stagger.
 */
const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className={`block overflow-hidden pb-[0.15em] ${className}`}>
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.2, delay, ease: [0.22, 1, 0.36, 1] }}
      className="block"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 800], [0, 120]);
  const scaleImg = useTransform(scrollY, [0, 800], [1, 1.08]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.35]);

  // Cursor-driven subtle parallax for a "3D feel"
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  useEffect(() => {
    const onMove = (e) => {
      const nx = (e.clientX / window.innerWidth - 0.5) * 2;
      const ny = (e.clientY / window.innerHeight - 0.5) * 2;
      setTilt({ x: nx, y: ny });
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#050505]"
    >
      {/* Background portrait — cropped to artist only + subtle cursor parallax */}
      <motion.div
        style={{
          y: yImg,
          scale: scaleImg,
          opacity,
          backgroundImage: `url(${BRAND.portrait})`,
          backgroundSize: "auto 165%",
          backgroundPosition: `${82 + tilt.x * 1.5}% ${30 + tilt.y * 1.5}%`,
          backgroundRepeat: "no-repeat",
          transition: "background-position 0.5s cubic-bezier(0.16, 1, 0.3, 1)",
        }}
        className="absolute inset-y-0 right-0 w-full lg:w-[55%]"
        aria-label="it's Malie portrait"
      />

      {/* Dark gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/92 to-[#050505]/30 lg:from-[#050505] lg:via-[#050505]/70 lg:to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/40" />

      {/* Ambient spotlights */}
      <div
        className="spotlight"
        style={{
          width: 500,
          height: 500,
          background: "rgba(245,158,11,0.18)",
          top: -120,
          right: -120,
        }}
      />
      <div
        className="spotlight"
        style={{
          width: 400,
          height: 400,
          background: "rgba(212,175,55,0.10)",
          bottom: -100,
          left: -80,
        }}
      />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 sm:px-10 pt-36 pb-16 lg:pt-44 lg:pb-24 min-h-[100svh] flex flex-col justify-between">
        <div className="grid lg:grid-cols-12 gap-10 flex-1 items-center">
          <div className="lg:col-span-7 max-w-2xl">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="flex items-center gap-4 mb-8"
            >
              <motion.span
                initial={{ scaleX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="h-px w-12 bg-[color:var(--gold)] origin-left"
              />
              <span className="overline">{BRAND.overline}</span>
            </motion.div>

            <h1
              data-testid="hero-headline"
              className="font-condensed text-[3.4rem] sm:text-[5rem] lg:text-[7.2rem] leading-[0.92] tracking-[-0.02em]"
            >
              <MaskedLine delay={0.3} className="text-white">
                {BRAND.hero_headline_lines[0]}
              </MaskedLine>
              <MaskedLine delay={0.5} className="shimmer-text">
                {BRAND.hero_headline_lines[1]}
              </MaskedLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9 }}
              className="mt-7 text-base sm:text-lg text-white/70 max-w-lg leading-relaxed"
            >
              {BRAND.short_bio}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.9 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a href="#music" data-testid="hero-stream-now" className="btn-gold">
                <Headphones size={16} strokeWidth={2} /> Stream Now
              </a>
              <a href="#videos" data-testid="hero-watch-video" className="btn-ghost">
                <Play size={14} strokeWidth={2} /> Watch Video
              </a>
              <a href="#booking" data-testid="hero-book-artist" className="btn-ghost">
                Book Artist
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.3, duration: 0.9 }}
              className="mt-12 flex items-center gap-6"
            >
              <span className="overline opacity-70">Tagline</span>
              <span className="font-display italic text-white/90 text-lg sm:text-xl">
                {BRAND.tagline}
              </span>
            </motion.div>
          </div>

          <div className="lg:col-span-5 hidden lg:block" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.9 }}
          className="flex flex-wrap items-end justify-between gap-8 pt-12"
        >
          <div className="flex items-center gap-5">
            <Waveform bars={36} className="h-7" />
            <span className="overline opacity-70">Now Playing — Head in the Sky</span>
          </div>

          <div className="flex items-center gap-5">
            <a
              href={STREAMING.spotify}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-social-spotify"
              className="w-11 h-11 grid place-items-center rounded-full border border-white/15 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors"
              aria-label="Spotify"
            >
              <Music2 size={16} />
            </a>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-social-instagram"
              className="w-11 h-11 grid place-items-center rounded-full border border-white/15 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors"
              aria-label="Instagram"
            >
              <Instagram size={16} />
            </a>
            <a
              href={STREAMING.youtubeMusic}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-social-youtube"
              className="w-11 h-11 grid place-items-center rounded-full border border-white/15 hover:border-[color:var(--gold)] hover:text-[color:var(--gold)] transition-colors"
              aria-label="YouTube"
            >
              <Youtube size={16} />
            </a>
          </div>

          <a
            href="#videos"
            data-testid="hero-scroll-indicator"
            className="hidden md:flex flex-col items-center gap-2 text-white/60 hover:text-white"
          >
            <span className="overline">Scroll</span>
            <ArrowDown size={16} className="float" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
